// The old pane's input queue behind an injected sender, with no HTTP route.
// One piece is in flight. A missing/refused/invalid acknowledgement stops
// input; no transport retry or later list replays bytes. A stop of kind
// `failed` may start fresh typing from the seq a fresh list observed
// (`resume`): by itself when no piece or size was in flight, else only on an
// explicit Reconnect (docs/terminal-api.md, Poulpe-603). What waited is dropped.
// The live client gives each piece its own intention UUID. Multiple views
// are arbitrated by the runner's exact last-input identity/byte check.
// No typed byte is logged or persisted; pending bytes live only in memory.

/** The most one request carries: a long paste leaves as several, in order. */
export const MAX_BATCH_BYTES = 16 * 1024
/** Bound local paste/type backlog while one piece awaits its acknowledgement. */
export const MAX_PENDING_BYTES = 1024 * 1024

/**
 * Why a terminal takes no more input: `gone`, the server confirms it was
 * closed or no longer exists; `refused`, the server would not take it, and
 * would not the next time either; `failed`, its delivery or acknowledgement is uncertain.
 */
export interface InputStop {
  reason: 'gone' | 'refused' | 'failed'
  /** The server's sentence, or the client's. */
  message: string
  /** A `failed` stop while a piece or a size was on its way: whether it arrived is unknown, so only an explicit Reconnect types again. */
  uncertain?: boolean
}

/** Sends one request; it resolves with the server's answer once the server took it, and rejects with its refusal. */
export type InputSender = (seq: number, data: string) => Promise<unknown>

const encoder = new TextEncoder()

/** What was typed or pasted, as the bytes a terminal reads: UTF-8. */
export function textBytes(text: string): Uint8Array {
  return encoder.encode(text)
}

/** A string that holds one byte in each character (xterm's `onBinary`: some mouse reports are no UTF-8). */
export function binaryBytes(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff
  return bytes
}

export function toBase64(bytes: Uint8Array): string {
  let binary = ''
  // In pieces: a call takes only so many arguments.
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000))
  return btoa(binary)
}

/**
 * A command as the keys that type it and run it: its lines, each ended by
 * Enter. A terminal reads a control character as a key (Ctrl-C, Escape,
 * Backspace), so what the command holds of them is left out: what runs is
 * what the block showed. A Tab would ask the shell to complete, and is typed
 * as a space. Empty when nothing is left to type.
 */
export function commandKeys(command: string): string {
  const lines = command
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, ' ')
    // C0 but the line feed, DEL and C1.
    .replace(/[\u0000-\u0009\u000b-\u001f\u007f-\u009f]/g, '')
    .replace(/^\n+/, '')
    .replace(/\s+$/, '')
  return lines === '' ? '' : `${lines.replace(/\n/g, '\r')}\r`
}

function statusOf(error: unknown): number | null {
  const status = typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined
  return typeof status === 'number' ? status : null
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'The server did not answer.'
}

export class InputQueue {
  private send: InputSender
  /** The `seq` of the next request. */
  private seq: number
  /** What waits for the request on its way to be answered, in the order it was typed. */
  private waiting: Uint8Array[] = []
  private waitingBytes = 0
  private busy = false
  private begun = false
  private stop: InputStop | null = null
  /** Raised by `resume`: what an older piece's answer says is not heard. */
  private generation = 0
  private readonly listeners = new Set<(stop: InputStop) => void>()

  constructor(send: InputSender, first = 1) {
    this.send = send
    this.seq = first
  }

  /** Why it takes no more input, or null while it does. */
  get stopped(): InputStop | null {
    return this.stop
  }

  /** The `seq` the next request will carry. */
  get next(): number {
    return this.seq
  }

  /** Refresh this same client's sender; auth replacement gets a separate queue. */
  use(send: InputSender): void {
    this.send = send
  }

  /** Initialise fresh typing from the observed next seq. Once this queue has
   * sent any input, a later observation cannot renumber or revive its bytes. */
  expect(seq: unknown): void {
    if (typeof seq !== 'number' || !Number.isSafeInteger(seq)) return
    if (!this.begun && !this.stop && seq >= 1 && seq > this.seq) this.seq = seq
  }

  /** Hear why it stopped, once, until the function it returns is called. */
  subscribe(listener: (stop: InputStop) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Type again after a `failed` stop, from `seq`, the next seq a fresh list
   * of the server says: its input reaches it again. An uncertain stop needs
   * `explicit`, a person's Reconnect. Nothing that waited is kept or sent
   * again. False, and nothing changes, when it cannot.
   */
  resume(seq: unknown, explicit = false): boolean {
    if (this.stop?.reason !== 'failed' || (this.stop.uncertain && !explicit)) return false
    if (typeof seq !== 'number' || !Number.isSafeInteger(seq) || seq < 1) return false
    this.generation += 1
    this.stop = null
    this.busy = false
    this.waiting = []
    this.waitingBytes = 0
    this.seq = seq
    return true
  }

  /** Bytes for the terminal, after all that was pushed before them. */
  push(bytes: Uint8Array): void {
    if (this.stop || bytes.length === 0) return
    if (bytes.length > MAX_PENDING_BYTES - this.waitingBytes) return this.fail('Too much terminal input is waiting. Input was stopped.')
    this.waitingBytes += bytes.length
    this.waiting.push(bytes)
    this.pump()
  }

  /** Let go of what waits and of what was scheduled: the terminal is closed. */
  close(): void {
    this.waiting = []
    this.waitingBytes = 0
    this.listeners.clear()
    // A failed stop becomes final too: a closed queue is never revived.
    if (!this.stop) this.stop = { reason: 'gone', message: 'The terminal is closed.' }
    else if (this.stop.reason === 'failed') this.stop = { reason: 'gone', message: this.stop.message }
  }

  /** The next request's bytes: all that waits, up to the most one request carries. */
  private take(): Uint8Array {
    let size = 0
    let count = 0
    while (count < this.waiting.length && size + this.waiting[count]!.length <= MAX_BATCH_BYTES) size += this.waiting[count++]!.length
    if (count === 0) {
      // The first piece alone is more than a request carries: its start leaves now, the rest waits in its place.
      const first = this.waiting[0]!
      this.waiting[0] = first.subarray(MAX_BATCH_BYTES)
      this.waitingBytes -= MAX_BATCH_BYTES
      return first.subarray(0, MAX_BATCH_BYTES)
    }
    this.waitingBytes -= size
    const batch = new Uint8Array(size)
    let at = 0
    for (const piece of this.waiting.splice(0, count)) {
      batch.set(piece, at)
      at += piece.length
    }
    return batch
  }

  private pump(): void {
    if (this.busy || this.stop || this.waiting.length === 0) return
    this.busy = true
    if (!Number.isSafeInteger(this.seq) || this.seq >= Number.MAX_SAFE_INTEGER) { this.fail('The terminal input sequence is exhausted.'); return }
    this.begun = true
    const seq = this.seq++
    this.deliver(seq, toBase64(this.take()))
  }

  private deliver(seq: number, data: string): void {
    const generation = this.generation
    let sent: Promise<unknown>
    try {
      sent = this.send(seq, data)
    } catch (error) {
      sent = Promise.reject(error)
    }
    sent.then(
      (answer) => {
        if (this.stop || generation !== this.generation) return
        this.busy = false
        const expected = typeof answer === 'object' && answer !== null ? (answer as { input_seq?: unknown }).input_seq : undefined
        if (expected !== seq + 1) return this.fail('The terminal input acknowledgement was not valid. Input was stopped.', false, true)
        this.pump()
      },
      (error: unknown) => {
        if (this.stop || generation !== this.generation) return
        const status = statusOf(error)
        if (status === 404) return this.end('gone', messageOf(error))
        // Another try would be answered the same: the server read the request and said no.
        if (status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429) return this.end('refused', messageOf(error))
        this.end('failed', messageOf(error))
      },
    )
  }

  /**
   * A stream or acknowledgement failure stops input without claiming a shell
   * exit. It is uncertain when a piece is on its way, or when the caller says
   * so (a size whose answer was lost).
   */
  fail(message: string, gone = false, uncertain = false): void {
    if (!this.stop || (gone && this.stop.reason !== 'gone')) this.end(gone ? 'gone' : 'failed', message, uncertain)
  }

  /** The server would refuse it again (its output connection was refused): no automatic revival. */
  refuse(message: string): void {
    if (!this.stop || this.stop.reason === 'failed') this.end('refused', message)
  }

  private end(reason: InputStop['reason'], message: string, uncertain = false): void {
    this.stop = reason === 'failed' && (uncertain || this.busy) ? { reason, message, uncertain: true } : { reason, message }
    this.waiting = []
    this.waitingBytes = 0
    this.busy = false
    for (const listener of [...this.listeners]) listener(this.stop)
  }
}
